import { useState } from "react";

import { PatientEdit, PatientEditState } from "@/Common/Components/Patient/PatientEdit";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { ButtonControl } from "../Buttons/ButtonControl";
import { Modal } from "../Modals/Modal";

import styles from "./CreatePatientPopup.module.scss";

interface ICreatePatientPopupProps {
    onCancel: () => void;
    onSave: (patientModel: IPatientCreationModel) => void;
    patientCreationModel: IPatientCreationModel;
    customOkButtonLabel?: string;
    customTitle?: string;
    displayCreatePatientLabel?: boolean;
}

export function CreatePatientPopup(props: ICreatePatientPopupProps): JSX.Element {
    const [patientCreationModel, setPatientCreationModel] = useState<IPatientCreationModel>(props.patientCreationModel ?? Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel()));
    const [isPatientCreateFormValid, setIsPatientCreateFormValid] = useState<boolean>(false);
    const { getText } = useTranslation();

    const notifyCreatePatientUpdate = (patientEditState: Readonly<PatientEditState>): void => {
        setPatientCreationModel(patientEditState.patientModel);
        setIsPatientCreateFormValid(PatientEditState.IsValid(patientEditState));
    };

    return (
        <Modal
            title={props.customTitle ?? getText("CreatePatientLabel").toTitleCase()}
            draggable
            showX
            maxHeight="85vh"
            onClose={() => props.onCancel()}
            showOverlay
            disableOverlayClose
            top={10}
            width={730}
            centerContent
            footer={(
                <div className={styles.footer}>
                    <ButtonControl
                        label={getText("CancelLabel")}
                        className="btn btn-default push-right-5"
                        onClick={() => props.onCancel()}
                    />
                    <ButtonControl
                        label={props.customOkButtonLabel ?? getText("CreateLabel")}
                        className="btn btn-primary push-right-5"
                        onClick={() => props.onSave(patientCreationModel)}
                        disabled={!isPatientCreateFormValid}
                    />
                </div>
            )}
        >
            <div>
                {props.displayCreatePatientLabel &&
                    <div className={styles.createPatientSubLabel}>{getText("CreatePatientFileLabel")}</div>
                }
                <PatientEdit
                    patientModel={patientCreationModel}
                    stateUpdateFunction={patientState => notifyCreatePatientUpdate(patientState)}
                    hideFooter={true}
                    duplicatePromptLabelName="DuplicateSentencePromptCalendarLabel"
                    enableQuickPatientConversionForDuplicates={true}
                />
            </div>
        </Modal>
    );
}
